<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { __ } from '@oat-sa-private/ui-core';
    import { Loading, Notification } from '@oat-sa-private/ui-components';
    import { Button } from '@oat-sa-private/ui-elements';
    import { isPausedByProctorExecution } from '../../util/proctoring.js';
    import { getTestStateStore } from '../../testsStateStore.js';

    /**
     * @typedef {Object} Props
     * @property {String} serviceCallId
     * @property {String} extraTimeStr
     * @property {Boolean} [hideContent]
     * @property {Function} onResume
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        serviceCallId,
        extraTimeStr,
        hideContent = false,
        onResume
    } = $props();

    // svelte-ignore state_referenced_locally
    const testStateStore = getTestStateStore(serviceCallId);
    let focusableResumeHeader = $state();
    let focusablePausedHeader = $state();

    let canResume = $derived($testStateStore && !isPausedByProctorExecution(testStateStore.getTestContext()));

    //to notify user that test can be resumed, focus h2 text once it appears
    //relies on the fact that elements will be recreated on condition change
    $effect(() => {
        if (focusableResumeHeader) {
            focusableResumeHeader.focus();
        } else if (focusablePausedHeader) {
            focusablePausedHeader.focus();
        }
    });
</script>

<style>
    .transition {
        @add-mixin flex-center-center;
        flex-direction: column;
        height: 100%;
        margin: 0 2rem;

        & :global(.notification-wrapper) {
            padding: 1rem 1.5rem;
        }
    }
    h2 {
        margin-bottom: 1rem;
        outline: none;
    }
    .notif-container {
        width: min(45rem, 100%);
    }
    .btn-container {
        padding-top: 4rem;
        width: min(45rem, 100%);
    }
</style>

<div class="proctor-wait transition">
    {#if !hideContent}
        {#if canResume}
            <h2 tabindex="-1" bind:this={focusableResumeHeader}>{__('Your test is ready')}</h2>
            {#if extraTimeStr}
                <div class="notif-container">
                    <Notification key={0} title={extraTimeStr} hierarchy="neutral" closeable={false} />
                </div>
            {/if}
            <div class="btn-container">
                <Button
                    name="proctor-resume"
                    label={__('Resume test')}
                    shape="pill"
                    size="small"
                    skin="primary"
                    fullwidth={true}
                    on:click={onResume} />
            </div>
        {:else}
            <h2 tabindex="-1" bind:this={focusablePausedHeader}>{__('Your test is paused')}</h2>
            <Loading text={__('Please wait for the proctor to resume your test.')} lite={false} />
        {/if}
    {/if}
</div>
